import { useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { articles, existingArticle, ArticleData } from "@/mocks/articles";
import SEOHead from "@/components/base/SEOHead";

const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://oopbuyspreadsheet.work';

function getArticle(slug: string): ArticleData | undefined {
  if (slug === "oopbuy-spreadsheet-complete-guide") {
    return existingArticle;
  }
  return articles.find((a) => a.slug === slug);
}

function buildArticleJsonLd(article: ArticleData, canonicalPath: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.imageUrl,
    "datePublished": "2026-06-01",
    "dateModified": "2026-06-20",
    "author": {
      "@type": "Organization",
      "name": "OOPBuy Editorial Team",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "OOPBuy Spreadsheet",
      "url": SITE_URL
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}${canonicalPath}`
    }
  };
}

export default function ArticlePage() {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();

  const slug = paramSlug || location.pathname.replace(/^\//, "").replace(/\/$/, "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticle(slug) : undefined;

  if (!article) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background-50">
        <SEOHead
          title="Article Not Found – OOPBuy Spreadsheet"
          description="The requested article could not be found. Browse our complete collection of OOPBuy spreadsheet guides, tutorials, and product sourcing resources."
        />
        <div className="text-center px-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  const canonicalPath = `/article/${article.slug}`;

  return (
    <main>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords="oopbuy spreadsheet, oopbuy spreadsheet guide, oopbuy spreadsheet tutorial, product sourcing spreadsheet, oopbuy spreadsheet 2026"
        canonicalPath={canonicalPath}
        ogType="article"
        ogImage={article.imageUrl}
        publishedTime="2026-06-01"
        modifiedTime="2026-06-20"
        jsonLd={buildArticleJsonLd(article, canonicalPath)}
      />
      <ArticleNavbar />
      <ArticleContent article={article} />
      <ArticleFooter />
    </main>
  );
}

function ArticleNavbar() {
  return (
    <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="flex items-center justify-between h-16 md:h-20 px-6 md:px-10 max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
            <i className="ri-database-2-line text-background-50 text-lg"></i>
          </div>
          <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900 whitespace-nowrap">
            OOPBuy Spreadsheet
          </span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Back to Home
        </Link>
      </div>
    </nav>
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <article className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-10 md:mb-14">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/" className="text-xs md:text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer">
            Home
          </Link>
          <span className="text-foreground-300 text-xs">/</span>
          <span className="text-xs md:text-sm text-foreground-500">Article</span>
        </div>
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-4">
          {article.title}
        </h1>
        <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed mb-4">
          {article.excerpt}
        </p>
        <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            {article.readTime}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            OOPBuy Editorial Team
          </span>
        </div>
      </header>

      {article.imageUrl && (
        <div className="relative rounded-2xl overflow-hidden mb-10 md:mb-14 aspect-[21/9]">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="space-y-8 md:space-y-10">
        {article.sections.map((section, idx) => (
          <SectionRenderer key={idx} section={section} />
        ))}
      </div>

      <footer className="mt-12 pt-8 border-t border-background-200/70">
        <p className="text-xs text-foreground-500 text-center">
          This article was last updated in {article.date}. For the latest OOPBuy spreadsheet information, visit the
          <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer ml-1">homepage</Link>.
        </p>
      </footer>
    </article>
  );
}

function SectionRenderer({ section }: { section: ArticleData["sections"][0] }) {
  switch (section.type) {
    case "heading":
      return (
        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            {section.heading}
          </h2>
        </section>
      );

    case "paragraph":
      return (
        <section>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            {section.content}
          </p>
        </section>
      );

    case "image":
      return (
        <section>
          <div className="relative rounded-2xl overflow-hidden aspect-[21/9]">
            <img
              src={section.imageUrl}
              alt={section.imageAlt || ""}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          {section.content && (
            <p className="text-xs text-foreground-500 text-center mt-2">{section.content}</p>
          )}
        </section>
      );

    case "tips":
      return (
        <section>
          {section.heading && (
            <h3 className="text-lg md:text-xl font-semibold text-foreground-800 mb-4">
              {section.heading}
            </h3>
          )}
          <ul className="space-y-3">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5 flex-shrink-0">
                  <i className="ri-check-line text-xs"></i>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      );

    case "steps":
      return (
        <section>
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              {section.heading}
            </h2>
          )}
          <div className="space-y-5">
            {section.stepItems?.map((step) => (
              <div key={step.number} className="flex gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm flex-shrink-0 mt-0.5">
                  {step.number}
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground-800 mb-1">{step.title}</h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case "faq":
      return (
        <section>
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              {section.heading}
            </h2>
          )}
          <div className="space-y-5">
            {section.faqItems?.map((faq, i) => (
              <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6">
                <h3 className="text-base md:text-lg font-semibold text-foreground-800 mb-2">
                  {faq.question}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
      );

    case "cta":
      return (
        <section className="bg-primary-50 rounded-2xl p-6 md:p-8 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
            Ready to Take the Next Step?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-5 max-w-lg mx-auto">
            {section.content}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              <i className="ri-shopping-cart-line"></i>
              Buy It Now
            </a>
          </div>
        </section>
      );

    default:
      return null;
  }
}

function ArticleFooter() {
  return (
    <footer className="bg-secondary-50 border-t border-background-200/70">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-database-2-line text-background-50 text-sm"></i>
            </div>
            <span className="font-heading text-base font-semibold text-foreground-900 whitespace-nowrap">
              OOPBuy Spreadsheet
            </span>
          </Link>
          <p className="text-xs text-foreground-500">
            &copy; 2026 OOPBuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
              kaspreadsheet.com
            </a>
            <Link to="/" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
              Home
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}